/* ─────────────────────────────────────────────────────────────────────────
 * Liquid glass: real refraction, baked into a displacement map.
 *
 * The glass is a slab whose top surface curves down to the backdrop across a
 * bezel band at the rim and is flat inside it. The curve is a convex squircle,
 * (1 − (1 − t)⁴)^¼, which stands vertical at the rim and eases into the flat
 * top without a ridge. Thickness over bezel is the surface slope, and it is
 * the real lever: the same descent spread over a wider band is a softer lens.
 *
 * For every pixel in that band the view ray, travelling straight down, meets
 * the surface at a known tilt, bends by Snell's law, and runs on through the
 * glass to the backdrop. How far sideways it lands is the offset baked into
 * the map. It always points INWARD, which is what gathers the scene toward the
 * middle and squeezes the world at the rim. The flat top bends nothing.
 *
 * The map is read by feDisplacementMap: red carries x, green carries y, 128 is
 * neutral. The filter must run with color-interpolation-filters="sRGB", or the
 * neutral 128 is linearised, decodes as an offset, and the whole backdrop
 * shears sideways. Three passes at slightly spread scales, one per colour
 * channel, give the chromatic fringe exactly where the light bends.
 * ───────────────────────────────────────────────────────────────────────── */

export interface GlassMapOptions {
    /* the element's box, px */
    width: number
    height: number
    /* corner radius, px; half the width on a square makes a disc */
    radius: number
    /* width of the band the surface curves across, px, clamped to the radius */
    bezel: number
    /* glass depth at full height, px */
    thickness: number
    /* refractive index, crown glass by default */
    ior?: number
}

export interface GlassMap {
    url: string
    width: number
    height: number
    /* the peak sideways offset in the map, px */
    peak: number
    /* the feDisplacementMap scale that turns a full channel swing into `peak` */
    scale: number
    /* true once the image has decoded, so swapping it in cannot flash an empty map */
    ready: boolean
}

export interface GlassFilterRefs {
    filter: SVGFilterElement | null
    feImage: SVGFEImageElement | null
    dispR?: SVGFEDisplacementMapElement | null
    dispG?: SVGFEDisplacementMapElement | null
    dispB?: SVGFEDisplacementMapElement | null
}

const IOR = 1.5
/* samples across the bezel; the profile is steep at the rim and needs them */
const SAMPLES = 1024
/* dispersion: each unit of chroma spreads the channels ±5% of the scale */
const DISPERSION = 0.05
/* maps are a function of their inputs only, so they are kept and reused */
const CACHE_LIMIT = 48
const XLINK = 'http://www.w3.org/1999/xlink'

const cache = new Map<string, GlassMap>()
/* the decoded probe for each map, held so the browser keeps the bitmap warm */
const probes = new WeakMap<GlassMap, HTMLImageElement>()

/* the surface height across the bezel, 0 at the rim and 1 at the flat top */
const surface = (t: number): number => Math.pow(1 - Math.pow(1 - t, 4), 0.25)

/* its derivative; vertical (infinite) at the rim itself */
const surfaceSlope = (t: number): number => {
    const u = 1 - t
    const base = 1 - u * u * u * u
    return base <= 0 ? Number.POSITIVE_INFINITY : (u * u * u) / Math.pow(base, 0.75)
}

/* Sideways offset at each depth into the band. The rim sample stays at 0:
 * the glass has no height there, so the ray has nowhere to travel. */
function buildProfile(bezel: number, thickness: number, ior: number): Float32Array {
    const out = new Float32Array(SAMPLES + 1)
    const steepness = thickness / bezel

    for (let i = 1; i <= SAMPLES; i++) {
        const t = i / SAMPLES
        const height = thickness * surface(t)
        const incidence = Math.atan(steepness * surfaceSlope(t))
        const refraction = Math.asin(Math.sin(incidence) / ior)
        out[i] = height * Math.tan(incidence - refraction)
    }

    return out
}

const keyOf = (values: number[]): string =>
    values.map((n) => Math.round(n * 100) / 100).join('|')

const setAttr = (el: Element, name: string, value: string): void => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value)
}

/** Bake (or fetch from the cache) the displacement map for one glass shape. */
export function makeGlassMap(options: GlassMapOptions): GlassMap | null {
    if (typeof document === 'undefined') return null

    const { width, height, radius, bezel, thickness, ior = IOR } = options
    if (!(width > 0 && height > 0)) return null

    const key = keyOf([width, height, radius, bezel, thickness, ior])
    const hit = cache.get(key)
    if (hit) {
        // most recently used moves to the back of the queue
        cache.delete(key)
        cache.set(key, hit)
        return hit
    }

    const w = Math.max(1, Math.round(width))
    const h = Math.max(1, Math.round(height))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const hw = width / 2
    const hh = height / 2
    const r = Math.max(0, Math.min(radius, hw, hh))
    const band = Math.max(0.5, Math.min(bezel, hw, hh))
    const profile = buildProfile(band, Math.max(0, thickness), ior)

    let peak = 0
    for (let i = 0; i <= SAMPLES; i++) {
        if (profile[i] > peak) peak = profile[i]
    }

    const image = ctx.createImageData(w, h)
    const data = image.data
    const innerX = hw - r
    const innerY = hh - r

    for (let y = 0; y < h; y++) {
        const dy = ((y + 0.5) * height) / h - hh
        const qy = Math.abs(dy) - innerY

        for (let x = 0; x < w; x++) {
            const dx = ((x + 0.5) * width) / w - hw
            const qx = Math.abs(dx) - innerX
            let red = 128
            let green = 128

            if (peak > 0) {
                // depth in from the edge, and the edge's outward normal
                let depth: number
                let nx = 0
                let ny = 0

                if (qx > 0 && qy > 0) {
                    const len = Math.hypot(qx, qy)
                    depth = r - len
                    nx = (Math.sign(dx) * qx) / len
                    ny = (Math.sign(dy) * qy) / len
                } else if (qx > qy) {
                    depth = r - qx
                    nx = Math.sign(dx)
                } else {
                    depth = r - qy
                    ny = Math.sign(dy)
                }

                if (depth > 0 && depth < band) {
                    const f = (depth / band) * SAMPLES
                    const i0 = Math.floor(f)
                    const i1 = i0 < SAMPLES ? i0 + 1 : SAMPLES
                    const k = (profile[i0] + (profile[i1] - profile[i0]) * (f - i0)) / peak
                    // sample from further IN: the offset opposes the outward normal
                    red = Math.round(128 - 127 * nx * k)
                    green = Math.round(128 - 127 * ny * k)
                }
            }

            const o = (y * w + x) * 4
            data[o] = red
            data[o + 1] = green
            data[o + 2] = 128
            data[o + 3] = 255
        }
    }

    ctx.putImageData(image, 0, 0)

    const map: GlassMap = {
        url: canvas.toDataURL('image/png'),
        width,
        height,
        peak,
        // feDisplacementMap offsets by scale × (C/255 − ½), so a channel swing
        // of 127 steps reaches `peak` when the scale is peak × 255/127
        scale: (peak * 255) / 127,
        ready: false
    }

    // Decode it before it is ever handed to the filter. Swapping an feImage to
    // a URL that has not decoded yet paints one frame with no map at all, which
    // feDisplacementMap reads as a full negative offset.
    const probe = new Image()
    const settle = (): void => {
        map.ready = true
    }

    probe.src = map.url
    if (typeof probe.decode === 'function') {
        probe.decode().then(settle, settle)
    } else {
        probe.onload = settle
        probe.onerror = settle
    }
    probes.set(map, probe)

    cache.set(key, map)
    if (cache.size > CACHE_LIMIT) {
        const oldest = cache.keys().next().value
        if (oldest !== undefined) cache.delete(oldest)
    }

    return map
}

/** Point the filter at a baked map. False until the map is ready to show. */
export function applyGlassMap(refs: GlassFilterRefs, map: GlassMap, chroma = 0): boolean {
    const { filter, feImage } = refs
    if (!filter || !feImage || !map.ready) return false

    const w = map.width.toFixed(2)
    const h = map.height.toFixed(2)

    // the filter region and the map are both exactly the element's own box
    for (const el of [filter, feImage]) {
        setAttr(el, 'x', '0')
        setAttr(el, 'y', '0')
        setAttr(el, 'width', w)
        setAttr(el, 'height', h)
    }

    if (feImage.getAttribute('href') !== map.url) {
        feImage.setAttribute('href', map.url)
        feImage.setAttributeNS(XLINK, 'xlink:href', map.url)
    }

    // blue bends more than red, so it gets the larger scale
    const spread = DISPERSION * chroma
    const passes: [SVGFEDisplacementMapElement | null | undefined, number][] = [
        [refs.dispR, 1 - spread],
        [refs.dispG, 1],
        [refs.dispB, 1 + spread]
    ]

    for (const [pass, k] of passes) {
        if (pass) setAttr(pass, 'scale', (map.scale * k).toFixed(3))
    }

    return true
}
